import React, {useState} from "react";
import {DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown} from "reactstrap";
import {Link} from "react-router-dom";
import '../i18/config';
import {useTranslation} from "react-i18next";
import {MainFileType} from "../redux/Main/File/types";
import {getMediaSources, importMediaSourceFiles, requestMediaSourcePickerToken} from "../api/Content/MediaSource";
import {MediaSourceImportType, MediaSourceType, waitForPickerToken} from "../helpers/mediaSourceEvents";
import {isGooglePickerConfigured, mimeTypesForAcceptedFiles, openGooglePicker} from "../helpers/googlePicker";
import {pendingFileFromImport} from "../helpers/mediaSourceImports";
import {showErrorNotification} from "../helpers/notifications";
import * as route from "../api/routes";
import {getHandlerIconByCode} from "../helpers/functions";
import {connectorHandlerCode} from "../constants/ConnectorHandler";

interface MediaSourceButtonProps {
    /** InputFile's acceptedFiles (".jpg, .png, .mp4"): the Picker only offers these types. */
    acceptedFiles?: string;
    isMultiple: boolean;
    disabled?: boolean;
    /** Names already in the field, so the same file is not added twice. */
    existingFileNames: string[];
    onPicked: (files: MainFileType[]) => void;
}

const newRequestId = (): string =>
    (typeof crypto !== 'undefined' && 'randomUUID' in crypto) ? crypto.randomUUID() : 'r' + Date.now() + Math.random().toString(36).slice(2);

/**
 * "Add from Google Drive": pick an account, then files in Google Picker. The files are imported by
 * the cabinet in the background and appear as importing until they arrive.
 */
const MediaSourceButton = ({acceptedFiles, isMultiple, disabled = false, existingFileNames, onPicked}: MediaSourceButtonProps) => {
    const {t, i18n} = useTranslation();
    const [sources, setSources] = useState<MediaSourceType[] | null>(null);
    const [isBusy, setBusy] = useState<boolean>(false);

    if (!isGooglePickerConfigured()) {
        return null;
    }

    const loadSources = () => {
        if (sources !== null) {
            return;
        }
        getMediaSources()
            .then((response: any) => setSources(response?.items || []))
            .catch(() => setSources([]));
    };

    const pickFrom = async (source: MediaSourceType) => {
        setBusy(true);
        try {
            const requestId = newRequestId();
            // Subscribe before asking: the answer can arrive before the request returns.
            const tokenPromise = waitForPickerToken(requestId);
            tokenPromise.catch(() => undefined);
            await requestMediaSourcePickerToken(source.uuid, requestId);

            let token;
            try {
                token = await tokenPromise;
            } catch (e: any) {
                showErrorNotification({message: e?.message === 'timeout'
                    ? t('peerActivity:mediaSourceNoAnswer')
                    : t('peerActivity:mediaSourceReconnect')});
                return;
            }

            const picked = (await openGooglePicker(String(token.accessToken), mimeTypesForAcceptedFiles(acceptedFiles), isMultiple, i18n.language))
                .filter(file => {
                    const isDuplicate = existingFileNames.some(name => name.toLowerCase() === file.name.toLowerCase());
                    if (isDuplicate) {
                        showErrorNotification({message: t('notification:fileAlreadyUploaded')});
                    }
                    return !isDuplicate;
                });

            if (picked.length === 0) {
                return;
            }

            const response: any = await importMediaSourceFiles(source.uuid, picked);
            const items: MediaSourceImportType[] = response?.items || [];

            onPicked(items.map((item, i) => pendingFileFromImport(item, picked[i]?.mimeType, picked[i]?.sizeBytes)));
        } catch (e) {
            showErrorNotification(e);
        } finally {
            setBusy(false);
        }
    };

    return (
        <UncontrolledDropdown className="mt-2" onToggle={loadSources} disabled={disabled || isBusy}>
            <DropdownToggle tag="button" type="button" className="btn btn-soft-primary btn-sm" disabled={disabled || isBusy}>
                {isBusy
                    ? <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"/>
                    : <img src={getHandlerIconByCode(connectorHandlerCode.CONNECTOR_HANDLER_GOOGLE_DRIVE)} alt="" height="14" className="me-1 align-text-bottom"/>
                }
                {t('peerActivity:mediaSourceAddFromDrive')}
            </DropdownToggle>
            <DropdownMenu>
                {sources === null &&
                    <DropdownItem disabled>{t('peerActivity:mediaSourceLoading')}</DropdownItem>
                }
                {sources !== null && sources.length === 0 &&
                    <DropdownItem tag={Link} to={route.CONTENT_SERVICES_LIST}>
                        {t('peerActivity:mediaSourceConnectFirst')}
                    </DropdownItem>
                }
                {(sources || []).map(source => (
                    <DropdownItem key={source.uuid} onClick={() => pickFrom(source)}>
                        {source.description || source.name}
                    </DropdownItem>
                ))}
            </DropdownMenu>
        </UncontrolledDropdown>
    );
};

export default MediaSourceButton;
